// src/components/CreateServiceNowIncidentModal.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { XCircle, AlertCircle, Check, ExternalLink, Settings } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useAuth } from "../contexts/AuthContext";

const CreateServiceNowIncidentModal = ({
  isOpen,
  onClose,
  remediation,
  remediationType,
  onSuccess
}) => {
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAssignmentGroup, setSelectedAssignmentGroup] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedImpact, setSelectedImpact] = useState("");
  const [selectedUrgency, setSelectedUrgency] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [serviceNowStatus, setServiceNowStatus] = useState(null);
  const apiCall = useApi();
  const navigate = useNavigate();
  const { canAdministerOrganization } = useRBAC();
  const { user } = useAuth();
  const isAdmin = canAdministerOrganization(remediation?.organizationId);

  // Priority options (ServiceNow standard)
  const priorityOptions = [
    { value: "1", label: "1 - Critical" },
    { value: "2", label: "2 - High" },
    { value: "3", label: "3 - Moderate" },
    { value: "4", label: "4 - Low" },
    { value: "5", label: "5 - Planning" }
  ];

  // Impact/Urgency options (ServiceNow standard)
  const impactUrgencyOptions = [
    { value: "1", label: "1 - High" },
    { value: "2", label: "2 - Medium" },
    { value: "3", label: "3 - Low" }
  ];

  const handleConfigureServiceNow = () => {
    onClose();
    navigate('/settings/integrations');
  };

  useEffect(() => {
    if (isOpen) {
      checkServiceNowStatus();
    }
  }, [isOpen]);

  const checkServiceNowStatus = async () => {
    setLoading(true);
    try {
      const status = await apiCall("GET", "/integrations/servicenow/status");
      setServiceNowStatus(status);

      if (status.isConfigured && !status.requiresReauthorization) {
        await Promise.all([
          fetchAssignmentGroups(),
          fetchCategories()
        ]);
        // Auto-map priority from remediation
        autoMapPriority();
      }
    } catch (err) {
      setError("Failed to check ServiceNow connection");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentGroups = async () => {
    try {
      const data = await apiCall("GET", "/servicenow/assignment-groups");
      setAssignmentGroups(data || []);
    } catch (err) {}
  };

  const fetchCategories = async () => {
    try {
      const data = await apiCall("GET", "/servicenow/categories");
      setCategories(data || []);
    } catch (err) {}
  };

  const autoMapPriority = () => {
    // Map Axaris priority to ServiceNow values
    const remPriority = getRemediationPriority()?.toUpperCase();
    switch (remPriority) {
      case 'CRITICAL':
        setSelectedPriority("1");
        setSelectedImpact("1");
        setSelectedUrgency("1");
        break;
      case 'HIGH':
        setSelectedPriority("2");
        setSelectedImpact("1");
        setSelectedUrgency("2");
        break;
      case 'MEDIUM':
        setSelectedPriority("3");
        setSelectedImpact("2");
        setSelectedUrgency("2");
        break;
      case 'LOW':
        setSelectedPriority("4");
        setSelectedImpact("3");
        setSelectedUrgency("3");
        break;
      default:
        setSelectedPriority("3");
        setSelectedImpact("2");
        setSelectedUrgency("2");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    setSubmitting(true);

    try {
      const result = await apiCall("POST", `/servicenow/remediations/${remediationType}/${getRemediationId()}/link`, {
        priority: selectedPriority || null,
        impact: selectedImpact || null,
        urgency: selectedUrgency || null,
        category: selectedCategory || null,
        assignmentGroup: selectedAssignmentGroup || null
      });

      if (result.success) {
        try {
          await apiCall("POST", `/allRemediations/logs/by-risk`, {
            remediationType,
            riskId: remediation.riskId,
            previousStatus: null,
            newStatus: result.incidentNumber || result.serviceNowIncidentNumber || "",
            comment: "ServiceNow incident linked",
            changedByUserName: user?.userName || user?.email || "",
            changeType: "ServiceNowLinked",
          });
        } catch (logErr) {}
        onSuccess?.(result);
        onClose();
      } else {
        setError(result.errorMessage || "Failed to create ServiceNow incident");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create ServiceNow incident");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions to get remediation data based on type
  const getRemediationId = () => {
    return remediation.remediationID;
  };

  const getRemediationTitle = () => {
    return remediation.remediationTitle ||
           remediation.enterpriseRemediationTitle ||
           remediation.tprmRemediationTitle ||
           remediation.ransomwareRemediationTitle ||
           remediation.darkWebRemediationTitle ||
           remediation.aiRemediationTitle ||
           remediation.grcRemediationTitle ||
           "Untitled Remediation";
  };

  const getRemediationPriority = () => {
    return remediation.remediationPriority ||
           remediation.enterpriseRemediationPriority ||
           remediation.tprmRemediationPriority ||
           remediation.ransomwareRemediationPriority ||
           remediation.darkWebRemediationPriority ||
           remediation.aiRemediationPriority ||
           remediation.grcRemediationPriority ||
           "MEDIUM";
  };

  const getServiceNowIncidentNumber = () => {
    return remediation.serviceNowIncidentNumber;
  };

  if (!isOpen) return null;

  // If already linked to ServiceNow, show the link
  if (getServiceNowIncidentNumber()) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-content-primary">ServiceNow Incident Linked</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XCircle size={24} />
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center mb-4">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-gray-700">This remediation is linked to ServiceNow incident</span>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-lg font-medium text-green-600">{getServiceNowIncidentNumber()}</p>
              {remediation.serviceNowIncidentUrl && (
                <a
                  href={remediation.serviceNowIncidentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-2 text-sm text-green-600 hover:text-green-800"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View in ServiceNow
                </a>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-content-primary">Create ServiceNow Incident</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={submitting}
          >
            <XCircle size={24} />
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : !serviceNowStatus?.isConfigured || serviceNowStatus?.requiresReauthorization ? (
          // ServiceNow not configured
          (<div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-medium">
                    ServiceNow is not configured
                  </p>
                  <p className="text-yellow-700 text-sm mt-1">
                    {isAdmin
                      ? "Configure the ServiceNow integration to create incidents from remediations."
                      : "An administrator needs to configure the ServiceNow integration before you can create incidents."}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              {isAdmin && (
                <button
                  onClick={handleConfigureServiceNow}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configure ServiceNow
                </button>
              )}
            </div>
          </div>)
        ) : (
          // ServiceNow configured - show form
          (<form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            {/* Remediation Summary */}
            <div className="mb-4 bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">Creating incident for:</p>
              <p className="font-medium text-content-primary">{getRemediationTitle()}</p>
            </div>
            {/* Assignment Group Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Group
              </label>
              <select
                value={selectedAssignmentGroup}
                onChange={(e) => setSelectedAssignmentGroup(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={submitting}
              >
                <option value="">-- Select Assignment Group (Optional) --</option>
                {assignmentGroups.map((group) => (
                  <option key={group.sysId} value={group.sysId}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Category Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={submitting}
              >
                <option value="">-- Select Category (Optional) --</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Priority Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={submitting}
              >
                <option value="">-- Select Priority --</option>
                {priorityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Auto-mapped from remediation priority: {getRemediationPriority()}
              </p>
            </div>
            {/* Impact Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Impact
              </label>
              <select
                value={selectedImpact}
                onChange={(e) => setSelectedImpact(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={submitting}
              >
                <option value="">-- Select Impact --</option>
                {impactUrgencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Urgency Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Urgency
              </label>
              <select
                value={selectedUrgency}
                onChange={(e) => setSelectedUrgency(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
                disabled={submitting}
              >
                <option value="">-- Select Urgency --</option>
                {impactUrgencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  "Create Incident"
                )}
              </button>
            </div>
          </form>)
        )}
      </div>
    </div>
  );
};

export default CreateServiceNowIncidentModal;
